import { useMicrophonePermission } from './useMicrophonePermission'
import { useSystemAudioPermission } from './useSystemAudioPermission'
import { useScreenRecordingPermission } from './useScreenRecordingPermission'
import { resolveNextPermission } from './audio-permission-prompt'
import { useGlobalState, useGlobalStore } from '@highlight/ipc/react'
import type { TranscriptionMethod } from '@renderer/chat/hooks/useAudioNoteSettings'
import type { PermissionsState } from '@shared/types/AudioCapturePermissions'
import {
  AUDIO_NOTES_TRANSCRIPTION_METHOD_KEY,
  AUDIO_NOTES_TRANSCRIPTION_RUNTIME_OVERRIDE_KEY,
  captureNeedsForPlan,
  resolveAudioMode,
  type CapturePlan,
  type TranscriptionRuntime,
} from '@shared/store/impl/audioSettings'
import { resolveRuntimeForSystemAudioStatus } from '@shared/utils/audio-notes/capture-runtime-fallback'

/**
 * The capture plan whose permissions the renderer should surface. A `plan`
 * from a main-process payload or deeplink wins; main already resolved it for
 * that capture. Otherwise mirror main's derivation: the settings-resolved
 * runtime plus the CATAP-unavailable fallback to Recall, judged from the
 * main-owned `permissions` mirror. Until the mirror has the CATAP status this
 * resolves to the nominal plan, which enables the system-audio hook in the
 * caller, whose initial check populates the mirror and re-renders us.
 */
function useResolvedCapturePlan(meetingAppName?: string, plan?: CapturePlan): CapturePlan {
  const [transcriptionMethod] = useGlobalStore<TranscriptionMethod>(
    'settings',
    AUDIO_NOTES_TRANSCRIPTION_METHOD_KEY,
    'cloud',
  )
  const [runtimeOverride] = useGlobalStore<TranscriptionRuntime>(
    'settings',
    AUDIO_NOTES_TRANSCRIPTION_RUNTIME_OVERRIDE_KEY,
  )
  const [permissions] = useGlobalState<PermissionsState>('permissions')
  if (plan) {
    return plan
  }
  const nominalRuntime = resolveAudioMode({
    method: transcriptionMethod ?? 'cloud',
    runtimeOverride,
    meetingAppName,
  }).runtime
  return resolveRuntimeForSystemAudioStatus(nominalRuntime, permissions?.systemAudioCaptureStatus)
}

/**
 * The macOS capture permissions the resolved plan needs: microphone always,
 * plus CATAP system audio (electron) or Screen Recording (recall). Missing
 * flags are false when that permission is not needed, so meeting surfaces can
 * share this hook without each re-deriving needs.
 */
export function useMissingAudioPermissions({
  meetingAppName,
  plan,
}: { meetingAppName?: string; plan?: CapturePlan } = {}) {
  const resolvedPlan = useResolvedCapturePlan(meetingAppName, plan)
  const needs = captureNeedsForPlan(resolvedPlan)
  const mic = useMicrophonePermission(needs.microphone)
  const systemAudio = useSystemAudioPermission(needs.systemAudio)
  const screenRecording = useScreenRecordingPermission(needs.screenRecording)

  const isLoaded =
    (!needs.microphone || mic.isLoaded) &&
    (!needs.systemAudio || systemAudio.isLoaded) &&
    (!needs.screenRecording || screenRecording.isLoaded)
  const micMissing = isLoaded && needs.microphone && mic.isApplicable && !mic.isAuthorized
  const systemAudioMissing = isLoaded && needs.systemAudio && systemAudio.isApplicable && !systemAudio.isAuthorized
  const screenRecordingMissing =
    isLoaded && needs.screenRecording && screenRecording.isApplicable && !screenRecording.isAuthorized

  return {
    needs,
    mic,
    systemAudio,
    screenRecording,
    isLoaded,
    micMissing,
    systemAudioMissing,
    screenRecordingMissing,
    nextPermission: resolveNextPermission({ micMissing, systemAudioMissing, screenRecordingMissing }),
  }
}
